/* eslint-disable camelcase */
import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import { css, StyleSheet } from 'aphrodite';

import async from 'async';
import moment from 'moment-timezone'
import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { AppStyles, AppTheme } from '../../../styles/AppTheme';
import { Helper } from '../../../common/helpers/Helper';
import { UiHelper } from '../../../common/helpers/UiHelper';
import { AppConfig } from '../../../AppConfig';
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta';
import { SearchBar } from '../../../common/components/SearchBar';
import { StatCard } from '../../../common/components/StatCard';
import { Controller } from '../../../common/Controller';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { TabChartCard } from '../../../common/components/charts/TabChartCard';
import { Fab, IconButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { CdpHelper } from '../../../helpers/CdpHelper';
import { CRMHelper } from '../../../helpers/CRMHelper';
import { AppHelper } from '../../../helpers/AppHelper';
import { CrmAccount } from './CrmAccount';
import { PageDialog } from '../../../common/components/PageDialog';
import { CustomToggleButton } from '../../../common/components/CustomToggleButton';
import { FilterHelper } from '@slx/slixta-common';
import { StaffHelper } from '../../../common/helpers/StaffHelper';

function getExportFieldDef(orgId) {
  const includeAllTime = AppHelper.isSettingEnabled(orgId, 'CRM_EXPORT_ALL_TIME')
  const useDateRange = AppHelper.isSettingEnabled(orgId, 'CRM_EXPORT_DATE_RANGE')
  const availableDateRanges = ['today', 'yesterday', 'this_week', 'this_month', 'last_7d', 'last_30d', 'last_90d', 'last_6m', 'last_12m', 'custom', 'all_time']

  function getExportMonths() {
    const MAX_MONTHS = 12
    const result = [];
    for (let i = 0; i < MAX_MONTHS; i++) {
      const m = moment().subtract(i, 'months');
      result.push({
        label: m.format('MMMM, YYYY'),
        key: m.format('MMYYYY')
      });
    }
    return result;
  }

  const exportOptions = getExportMonths()

  if (useDateRange) {
    exportOptions.push({key: 'date_range', label: 'Custom Date Range'})
  } else if (includeAllTime) {
    exportOptions.unshift({key: 'all_time', label: 'All Time'})
  }

  const monthField = {
    key: 'mmyyyy',
    type: 'radio',
    label: 'Month',
    tip: 'Choose the month for export',
    options: exportOptions,
    v_required: {
      value: true,
      err: 'Choose a month'
    },
    placement: 'left',
    defaultValue: exportOptions[0].key,
  }

  const fieldDef = {
    title: 'Export',
    ctaButtonText: 'Export',
    steps: [
      {
        fields: [monthField]
      }
    ]
  }

  if (useDateRange) {
    fieldDef.steps[0].fields.push(
      {
        condition: { key: 'mmyyyy', values: ['date_range'] },
        key: 'exportMode',
        type: 'radio',
        label: 'Filter by',
        tip: 'Filter accounts by created or modified date',
        options: [
          { key: 'CREATED', label: 'CREATED' },
          { key: 'MODIFIED', label: 'MODIFIED' },
        ],
        v_required: {
          value: true,
          err: 'Choose a mode'
        },
        placement: 'left',
        defaultValue: 'CREATED',
        advanced: true,
      },
      {
        condition: { key: 'mmyyyy', values: ['date_range'] },
        key: 'exportDateRange',
        type: 'dateRange',
        label: 'Date Range',
        tip: 'Choose the date range for export',
        availableDateRanges,
        timezone: Helper.getLocalTimezone(),
        v_required: {
          value: true,
          err: 'Choose a date range'
        },
        placement: 'right',
      }
    )
  }

  return fieldDef
}

function getImportFieldDef(staffUsers, currentStaffUser, orgId, crmcontactFieldDef) {
  const tagsField = Helper.getField(crmcontactFieldDef, 'tags')
  const fieldDef = {
    title: 'Import',
    ctaButtonText: 'Import',
    steps: [
      {
        fields: [
          {
            key: 'importCsv',
            linkName: 'importCsvLink',
            type: 'document',
            subType: 'document',
            label: 'Import CSV',
            tip: 'Select a csv file to bulk import up to 100 contacts; Supported fields are \'email\', \'phone\', \'company\', \'firstname\', \'lastname\', \'tags\', \'linkedinurl\', \'jobtitle\', \'timezone\', \'leadstate\'; By default all contacts will be imported with state PROSPECT',
            tipLink: {
              title: 'Sample File',
              src: AppConfig.BULK_IMPORT_SAMPLE_FILE_URL
            },
            v_required: { value: true, err: 'Provide csv file' },
            placement: 'left',
            documentUpload: true,
            fileSizeLimit: 1048576,
            uploadAccept: 'text/csv',
          },
          {
            key: 'updateExisting',
            type: 'radio',
            label: 'Update Existing',
            tip: 'Disable this if you do not want existing CRM contacts to be updated',
            options: [{key: 'YES', label: 'YES'}, {key: 'NO', label: 'NO'}],
            v_required: {
              value: true,
              err: 'Choose a value'
            },
            placement: 'right',
            defaultValue: 'NO',
          },
          {
            key: 'csvMapping',
            linkName: 'csvMappingLink',
            type: 'document',
            subType: 'document',
            label: 'CSV Mapping',
            tip: 'Optionally provide a csv file to map the fields of your input csv to supported fields in Slixta',
            v_required: { value: false, err: 'Provide csv mapping file' },
            placement: 'left',
            documentUpload: true,
            fileSizeLimit: 1048576,
            uploadAccept: 'text/csv',
          },
          {
            condition: { key: 'updateExisting', value: 'NO', values: ['NO'] },
            key: 'assignee',
            type: 'radio',
            label: 'Default Assignee',
            tip: 'Optionally provide the default assignee for imported contacts',
            options: [],
            v_required: {
              value: false,
              err: 'Provide Assignee'
            },
            placement: 'right'
          },
          {
            condition: { key: 'updateExisting', value: 'NO', values: ['NO'] },
            key: 'defaultTags',
            type: 'check_box',
            label: 'Default Tags',
            tip: 'Optionally provide default tags for imported contacts',
            options: tagsField && tagsField.options ? tagsField.options : [],
            v_required: {
              value: false,
              err: 'Provide Default Tags'
            },
            placement: 'left'
          },
        ]
      }
    ]
  }
  UiHelper.populateOptions(fieldDef, {items: StaffHelper.uniqueStaffByUserId(staffUsers)}, 'assignee')
  if (currentStaffUser && currentStaffUser.id) {
    const assigneeField = fieldDef.steps[0].fields.find(f => f.key === 'assignee')
    assigneeField.defaultValue = currentStaffUser.id
  }
  return fieldDef
}

function getSaveFilterFieldDef(customFilters) {
  const fieldDef = {
    formType: 'add',
    title: 'Save Filter',
    ctaButtonText: 'Save',
    steps: [
      {
        fields: [
          {
            key: '_chosenFilter',
            type: 'radio',
            label: 'Choose Filter',
            tip: 'Save as new filter or overwrite an existing custom filter',
            v_required: { value: true, err: 'Choose a filter' },
            placement: 'left',
            options: [],
            defaultValue: '_new',
          },
          {
            condition: { key: '_chosenFilter', value: '_new', values: ['_new'] },
            key: 'filterNickname',
            type: 'edit_text',
            label: 'Filter Nickname',
            tip: 'Nickname of the new filter',
            v_required: { value: true, err: 'Provide a filter nickname' },
            placement: 'left',
          },
        ]
      }
    ]
  }
  const options = (customFilters || []).map(f => ({ key: f.label, label: f.label }))
  options.unshift({ key: '_new', label: '[Save as New Filter]' })
  fieldDef.steps[0].fields.find(f => f.key === '_chosenFilter').options = options
  return fieldDef
}

function constructFilterFormDefConf({fieldDef,crmBulkImportJobs,currentUser,orgId}) {
  const currentUrl = document.location.href
  const showInternal = Helper.isShowInternal()
  const customFields = CdpHelper.generateCustomFields(fieldDef, 'left')
  const advancedCustomFieldKeys = CRMHelper.getAdvancedCustomFieldKeys(customFields)
  const dateRangeFieldsConf = {}
  if (Array.isArray(customFields) && customFields.length > 0) {
    customFields.filter((field) => field.type === 'dateRange').forEach((field) => {
      dateRangeFieldsConf[field.key] = {
        timezone: Helper.getLocalTimezone(),
        availableDateRanges: field.availableDateRanges
      }
    })
  }
  const _url = new URL(currentUrl)
  const filterFormDefConf = {
    fields: {
      tags: (() => {
        const fieldData = {}
        const tagsField = JSON.parse(JSON.stringify(Helper.getField(fieldDef, 'tags')));
        if (tagsField && tagsField.options && !showInternal) {
          fieldData.options = tagsField.options.filter(option => option.key !== 'INTERNAL');
        }
        return fieldData
      })(),
      assignee: (() => {
        const fieldData = {}
        const assigneeField = JSON.parse(JSON.stringify(Helper.getField(fieldDef, 'assignee')))
        if(assigneeField && assigneeField.options) {
          assigneeField.options.unshift({key: 'current-user', text: '[CURRENT USER]'})
          fieldData.options = assigneeField.options
        }
        fieldData.optionsValueMapping = currentUser && {'current-user': currentUser }
        return fieldData
      })(),
      importJobId: {
        options: crmBulkImportJobs && crmBulkImportJobs.map(j => ({key: j.id, label: Helper.formatTimestamp(j.createdTs)}))
      },
      createdTs: {
        timezone: (_url && _url.searchParams && _url.searchParams.has('_datetimezone')) ? _url.searchParams.get('_datetimezone') : Helper.getLocalTimezone(),
        disabled: (_url && _url.searchParams && _url.searchParams.has('_datetimezone')) ? true : false,
        availableDateRanges: ['today', 'yesterday', 'this_week', 'this_month', 'last_7d', 'last_30d', 'last_90d', 'last_6m', 'last_12m', 'current_7d', 'current_30d', 'custom', 'all_time']
      },
      ...dateRangeFieldsConf
    },
    includedFilters: (()=> {
      const includedKeys = ['tags']
      includedKeys.push(...customFields.map(f => f.key))
      includedKeys.push('createdTs','importJobId')
      if (!AppHelper.isRestrictViewToAssignedAccounts(orgId)) {
        includedKeys.push('assignee')
      }
      return includedKeys
    })(),
    advancedFilters: ['createdTs', 'importJobId', ...advancedCustomFieldKeys]
  }
  return filterFormDefConf
}

function getPreviewFromFields(account, fieldDef, previewFields) {
  if (account && previewFields && previewFields.length > 0 && previewFields.flat().length > 0) {
    const isMobileView = Helper.isMobileView()
    const lines = []

    const groups = Array.isArray(previewFields[0]) ? previewFields : [previewFields]

    groups.forEach(fieldsGroup => {
      const values = []

      fieldsGroup.forEach(field => {
        let value = ''
        let label = ''
        let showLabel = false

        if (field.startsWith('+')) {
          showLabel = true
          field = field.slice(1)
        }

        if (!isMobileView || !['createdTs'].includes(field)) {
          const customField = Helper.getCustomField(fieldDef, field)
          if (customField && customField.key && account[customField.key]) {
            label = customField.label || ''
            if (customField.type === 'date_yyyymmdd') {
              value = moment(account[customField.key]).format('DD-MMM-YYYY')
            } else if (customField.type === 'dateTime') {
              value = Helper.formatTimestamp(account[customField.key])
            } else {
              value = account[customField.key]
            }
          } else if (account[field]) {
            const regularField = Helper.getField(fieldDef, field)
            if (regularField) {
              label = regularField.label || ''
            }
            value = account[field]
          }
        }

        value = Array.isArray(value) ? value.join(', ') : value
        value = label && value && showLabel ? (
          <span>
            <span className={css(Styles.previewLabel)}>{label}:</span> {value}
          </span>
        ) : (value)

        if (value) values.push(value)
      })

      if (values.length > 0) lines.push(values)
    })

    if (isMobileView) {
      return (
        <div className={css(Styles.spanValue, Styles.infoCellMobile)}>
          {lines.map((values, idx) => (
            <span key={idx} className={css(Styles.infoFieldMobile)}>
              {values.map((spanText, i) => (
                <Fragment key={i}>
                  {spanText}
                  {i < values.length - 1 && ' | '}
                </Fragment>
              ))}
            </span>
          ))}
          <span className={css(Styles.line3)}>{Helper.formatTimestamp(account.createdTs)}</span>
        </div>
      )
    }

    return (
      <div className={css(Styles.spanValue)}>
        {lines.map((values, lineIndex) => (
          <div key={lineIndex}>
            {values.map((v, i) => (
              <Fragment key={i}>
                {v}
                {i < values.length - 1 && ' | '}
              </Fragment>
            ))}
          </div>
        ))}
      </div>
    )
  }
}


function getAccountInfoCellSpan(account) {
  let preSpanText = ''
  const postSpanText = ''
  preSpanText += `${account.title ? account.title : ''}`
  return <span className={css(Styles.spanValue)}>{preSpanText}{postSpanText}</span>
}

function getAccountInfoCellSpanMobile(account) {
  return(
    <div className={css(Styles.spanValue, Styles.infoCellMobile)}>
      {account.title && <span class={css(Styles.infoFieldMobile)}>{account.title}</span>}
      <span className={css(Styles.line3)}>{Helper.formatTimestamp(account.createdTs)}</span>
    </div>
  )
}

export class CrmAccounts extends BaseEntityPageSlixta {

  constructor(props) {
    super(props);
    this.emptyText = 'No results. Check your search query or search filters.'
    this.pageKey = 'crm-accounts'
    this.pageTitle = Helper.getString('crm-accounts')
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.subOrgId = Controller.get().userMgr().getSubOrgId(this.orgId)
    this.staffUserId = Controller.get().getUserId()
    this.currentUser = this.staffUserId
    this.isMobileView = Helper.isMobileView()
    this.apiData = {
      orgId: this.orgId,
    }
    this.staffUsers = []
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: this.pageTitle },
    ]
    this.attribsGroupId = 'settingscrmcontactattrs' + this.orgId

    this.xLabelsDifference = {
      today: 2,
      yesterday: 2,
      this_week: 1,
      this_month: 1,
      last_7d: 1,
      last_30d: 4,
      last_90d: 6,
      last_6m: 1,
      last_12m: 2,
      current_7d: 1,
      current_30d: 4,
      current_90d: 6,
      current_6m: 1,
      current_12m: 2,
      next_7d: 1,
    }
    this.dateMap = {
      today: '1h',
      yesterday: '1h',
      this_week: '1d',
      this_month: '1d',
      last_7d: '1d',
      last_30d: '1d',
      last_90d: '1d',
      last_6m: '1M',
      last_12m: '1M',
      current_7d: '1d',
      current_30d: '1d',
      current_90d: '1d',
      current_6m: '1M',
      current_12m: '1M',
      next_7d: '1d',
    }
    this.defaultDateInteval = {id: 'last_30d'}
    this.aggregationTermsLimit = 100
    this.maxPaginationItems = 10000

    this.restrictViewCrmFilters = {
      'tags!': 'INTERNAL',
    }
    if (AppHelper.isRestrictViewToAssignedAccounts(this.orgId)) {
      this.restrictViewCrmFilters.assignee = this.staffUserId
    }

    this.toFetch = [
      { key: 'crmaccounts', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['crmaccounts'], queryParams: { orgId: this.orgId, pb: 'fieldDef', includeCustomFields: 'true', limit: 100, ...this.restrictViewCrmFilters, _datetimezone: Helper.getLocalTimezone(), _dateinterval: this.dateMap[this.defaultDateInteval.id], _aggTermsLimit: this.aggregationTermsLimit } },
      { key: 'crmcontactattrs', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['items', `${this.attribsGroupId}-all`], queryParams: { orgId: this.orgId, groupId: this.attribsGroupId } },
      { key: 'orgusers', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-org-users2'], queryParams: { orgId: this.orgId, parentOrgId: this.orgId } },
      { key: 'usersME', method: 'GET', endPoint: ApiHelper.makeUrlPath(['users', 'me'], {}) },
      { key: 'crmbulkimportjobs', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-crm-jobs'], queryParams: { orgId: this.orgId } },
    ]
    if (AppHelper.isTeamsEnabled(this.orgId) && !this.subOrgId) {
      this.toFetch.push({ key: 'suborgs', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['miscs', 'get-sub-orgs'], queryParams: {orgId: this.orgId, parentOrgId: this.orgId } })
    }
    this.cellWidths = {
      Index: Styles.cellWidthSmall,
      Info: Styles.cellWidthNormal,
      Info2: Styles.cellWidthNormal,
    }
    this.tableHeadersMap = {
      ...(this.isMobileView ? {} : {
        Index: (current) => current.__index + 1,
      }),
      Info: (current) => {
        let previewFields = null
        const accountPreviewTemplate = AppHelper.getSetting(this.orgId, 'CRM_ACCOUNT_PREVIEW_TEMPLATE')
        if(accountPreviewTemplate) {
          previewFields = accountPreviewTemplate.split('|')
            .map(part =>part.split(',')
              .map(item =>item.trim())
              .filter(item => item !== '')
            ).filter(arr => arr.length > 0)
        }
        const fieldDef = (previewFields && previewFields.flat().length > 0) ? this.accountPreviewFieldDef : null
        return (
          <div>
            <Fragment>
              {previewFields && Array.isArray(previewFields) && previewFields.flat().length > 0 && getPreviewFromFields(current,fieldDef,previewFields)}
              {(!previewFields || previewFields.length === 0) && !this.isMobileView && getAccountInfoCellSpan(current)}
              {(!previewFields || previewFields.length === 0) && this.isMobileView && getAccountInfoCellSpanMobile(current)}
              {!this.isMobileView && (!previewFields || previewFields.length === 0) && <br />}
            </Fragment>
            {!this.isMobileView && <Fragment>
              <span className={css(Styles.line3)}>{Helper.formatTimestamp(current.createdTs)}</span>
              <br />
            </Fragment>}
          </div>
        )
      },
      ...(this.isMobileView ? {} : {
        Info2: (current) => {
          const assigneeUser = CRMHelper.findStaffByAssignee(this.staffUsers, current.assignee)
          const assigneeName = assigneeUser ? Helper.formatName(assigneeUser) : ''
          return (
            <div>
              {!this.isMobileView && assigneeName &&
                <Fragment>
                  <span className={css(Styles.line2)}>Assignee: {assigneeName}</span>
                  <br />
                </Fragment>
              }
              {!this.isMobileView && Array.isArray(current.tags) && current.tags.length > 0 &&
                <Fragment>
                  <span className={css(Styles.line2)}>Tags: {current.tags.join(', ')}</span>
                  <br />
                </Fragment>
              }
            </div>
          )
        }
      }),
    }
    this.noTableHead = true

    this.headerActions = []
    this.headerActions.push({
      label: 'Add',
      tooltip: 'Add Account',
      icon: 'add',
      type: 'icon-label',
      onClick: () => this.openModalForAdd(),
    })
    if (!this.isMobileView) {
      this.headerActions.push({
        label: 'Import',
        tooltip: 'Import',
        icon: 'group_add',
        type: 'icon-label',
        onClick: () => {
          const fieldDef = getImportFieldDef(this.staffUsers, this.currentStaffUser, this.orgId, this.fieldDef)
          fieldDef.title = Helper.getString('importAccounts')
          this.openModalForAdd(fieldDef)
        }
      })
    }
    this.headerActions.push({
      label: 'Refresh',
      tooltip: 'Refresh',
      icon: 'refresh',
      type: 'icon-label',
      onClick: () => this.fetchItems(),
    })

    this.state.searchFilters = {}
    this.state.filterOptions = []
    this.state.selectedTabData = {
      tabbedChart1: 0,
      tabbedChart2: 0,
    }

    this.uploadMs = AppConfig.CDP_MS.ENDPOINT
    this.displayCheckBoxTable = !this.isMobileView
    this.checkboxTableItemNameSingular = 'account'
    this.checkboxTableItemNamePlural = 'accounts'
    this.isHeaderActionsHidden = this.isMobileView ? true : false
    this.state.compactStatsTabSelected = 0
  }

  componentDidMount() {
    this.fetchItems()
  }

  openInteractionsDialog(current) {
    if(current) {
      this.setState({
        entityDialogProps: {
          match: {params: {orgId: this.orgId, id: current.id}},
          hideBreadCrumbs: true,
          isLogoForMobileHidden: true,
          isHeaderTitleHidden: true,
          hideHeaderbar: true,
          pageTitle: current.title,
        },
        entityId: current.id,
        isEntityDialogOpen: true,
        entityDialogFullHeight: !this.isMobileView && true,
        entityDialogFullWidth: true,
        entityDialogTitle: current.title,
      })
    }
  }

  getXLabelsDifference(timezone) {
    let xLabelsDifference = this.xLabelsDifference[this.defaultDateInteval.id]
    if(this.state && this.state.searchFilters && this.state.searchFilters.createdTs && this.state.searchFilters.createdTs.id) {
      if(this.state.searchFilters.createdTs.id === 'custom') {
        const countOfDays = CRMHelper.getCountOfDaysInCustomDateRange(this.state.searchFilters.createdTs, timezone)
        if (countOfDays === 1) {
          xLabelsDifference = 2
        } else if (countOfDays <= 7) {
          xLabelsDifference = 1
        } else if (countOfDays <= 30) {
          xLabelsDifference = 4
        }  else if (countOfDays <= 90) {
          xLabelsDifference = 6
        } else if (countOfDays <= 180) {
          xLabelsDifference = 1
        } else if (countOfDays <= 365) {
          xLabelsDifference = 2
        } else {
          xLabelsDifference = 1
        }
      } else {
        xLabelsDifference = this.xLabelsDifference[this.state.searchFilters.createdTs.id]
      }
    }
    return xLabelsDifference
  }

  getDateInterval(searchFilters, timezone) {
    let dateInterval = this.dateMap[this.defaultDateInteval.id]
    if(searchFilters && searchFilters.createdTs && searchFilters.createdTs.id) {
      if(searchFilters.createdTs.id === 'custom') {
        const countOfDays = CRMHelper.getCountOfDaysInCustomDateRange(searchFilters.createdTs, timezone)
        if (countOfDays === 1) {
          dateInterval = '1h'
        } else if (countOfDays > 90) {
          dateInterval = '1M'
        }
        else{
          dateInterval = '1d'
        }
      } else {
        dateInterval = this.dateMap[searchFilters.createdTs.id]
      }
    }
    return dateInterval
  }

  applyCrmAccountFieldDefOptions(fieldDef) {
    if (!fieldDef) return
    CRMHelper.enrichCrmAccountFormFieldDefOptions(fieldDef, {
      stats: this.crmAccountsStats || (this.state && this.state.stats),
      crmContactAttrs: this.crmContactAttrs,
    })
  }

  /** Merge list stats/option catalogs into the full account fieldDef (do not replace schema — custom fields). */
  mergeCrmAccountsListOptionCatalogs(targetFieldDef, sourceFieldDef) {
    if (!targetFieldDef || !sourceFieldDef) return
    CRMHelper.copySelectOptionsFromBaseFieldDef(targetFieldDef, sourceFieldDef)
    if (sourceFieldDef.options) {
      targetFieldDef.options = {
        ...(targetFieldDef.options || {}),
        ...sourceFieldDef.options,
      }
    }
    CRMHelper.hydrateFieldDefSelectOptionsFromMap(targetFieldDef)
  }

  refreshCrmAccountFieldDefOptions(fieldDef) {
    if (!fieldDef) return
    Helper.loadAttrOptions(fieldDef, this.crmContactAttrs, 'tags')
    const tagsField = Helper.getField(fieldDef, 'tags')
    if (tagsField && tagsField.options) {
      tagsField.options = tagsField.options.sort((a, b) => a.label.localeCompare(b.label))
    }
    this.applyCrmAccountFieldDefOptions(fieldDef)
    UiHelper.populateOptions(fieldDef, { items: StaffHelper.uniqueStaffByUserId(this.staffUsers) }, 'assignee')
  }

  syncCrmAccountsListFieldDefAndStats(listResult) {
    if (!listResult) return
    if (listResult.stats) {
      this.crmAccountsStats = listResult.stats
      if (this.crmAccountsResult) {
        this.crmAccountsResult.stats = listResult.stats
      }
    }
    const incomingFieldDef = listResult.fieldDef
    if (incomingFieldDef) {
      if (!this.fieldDef) {
        this.fieldDef = this.formDefinition = incomingFieldDef
        if (this.crmAccountsResult) {
          this.crmAccountsResult.fieldDef = incomingFieldDef
        }
      } else {
        this.mergeCrmAccountsListOptionCatalogs(this.fieldDef, incomingFieldDef)
        if (this.crmAccountsResult && this.crmAccountsResult.fieldDef) {
          this.mergeCrmAccountsListOptionCatalogs(this.crmAccountsResult.fieldDef, incomingFieldDef)
        }
      }
    }
    if (this.fieldDef) {
      this.refreshCrmAccountFieldDefOptions(this.fieldDef)
    } else if (listResult.stats) {
      this.applyCrmAccountFieldDefOptions(this.fieldDef)
    }
  }

  fetchItem() {
    this.apiStartTs = Helper.currentTs()
    const accountId = this.state.dialogApiData && this.state.dialogApiData.responseBody && this.state.dialogApiData.responseBody.id
    const searchFiltersQuery = CRMHelper.constructSearchFiltersQuery(this.state.searchFilters, this.state.filterOptions, this.state.showInternal, this.restrictViewCrmFilters.assignee)
    const queryParams = {
      orgId: this.orgId,
      query: this.state.searchText || '',
      pb: 'fieldDef',
      includeCustomFields: 'true',
      limit: 1,
      ...this.restrictViewCrmFilters,
      _datetimezone: Helper.getLocalTimezone(),
      _dateinterval: this.getDateInterval(this.state.searchFilters, this.state.timezone),
      _aggTermsLimit: this.aggregationTermsLimit,
      ...searchFiltersQuery,
    }
    const listFetch = { key: 'crmaccounts', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['crmaccounts'], queryParams }
    const fcns = []
    if (accountId) {
      fcns.push({ key: 'crmaccount', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['crmaccounts', accountId], queryParams: { orgId: this.orgId, pb: 'fieldDef' } })
    }
    fcns.push(listFetch)

    const callParallel = fcns.map((f) => (callback) => ApiHelper.call(f, callback))

    async.parallel(callParallel, async function (err, results) {
      if (err) {
        this.setState({
          fetchState: ApiHelper.State.ERROR,
          loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
          errMsg: Helper.getErrorMsg(err)
        })
        return
      }

      const accountResult = accountId ? results[0] : null
      const listResult = accountId ? results[1] : results[0]
      this.syncCrmAccountsListFieldDefAndStats(listResult)
      let updatedItems = this.state.items
      if (accountResult && accountResult.id) {
        const { fieldDef, _apiVersion, ...updatedResult } = accountResult
        updatedItems = this.state.items.map(item => item.id === accountResult.id ? updatedResult : item)
      }
      this.setState({
        items: updatedItems,
        stats: listResult && listResult.stats,
        cards: (listResult && Array.isArray(listResult.cards)) ? listResult.cards.map(c => ({...c, key: c.key || c.label.toLowerCase()})) : null,
        timezone: (listResult && listResult._datetimezone) || Helper.getLocalTimezone(),
        fetchState: ApiHelper.State.READY,
        loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD,
      })
    }.bind(this))
  }

  onCompleteAction(options) {
    if(this.formDefinition && this.formDefinition.loadingMode === ApiHelper.LOADING_MODE.INSTANT_LOAD) {
      this.setState({ modalOpen: false, dialogApiData: options.apiInvokeData, loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD })
      this.fetchItem()
    } else {
      this.setState({ modalOpen: false, items: [], dialogApiData: options.apiInvokeData })
      this.fetchItems()
    }
  }

  onFetchSuccess(results, options) {
    const { fetchNext } = options || {}
    if (!fetchNext) {
      this.crmAccountsResult = this.getResult(results, 'crmaccounts')
      this.fieldDef = this.formDefinition = this.crmAccountsResult.fieldDef
      this.crmAccountsStats = this.crmAccountsResult.stats
      this.crmContactAttrs = this.getResult(results, 'crmcontactattrs')
      this.staffUsers = this.getResult(results, 'orgusers').items || []
      this.currentStaffUser = this.getResult(results, 'usersME')
      this.crmBulkImportJobs = this.getResult(results, 'crmbulkimportjobs').items
      this.subOrgs = this.getResult(results, 'suborgs') ? this.getResult(results, 'suborgs').items : []
      const showTenantNameInHeader = AppHelper.isSettingEnabled(this.orgId, 'TENANT_NAME_IN_HEADER')
      if (showTenantNameInHeader) {
        this.pageTitle = UiHelper.getOrgTitle() || this.pageTitle
      }
      Helper.loadAttrOptions(this.fieldDef, this.crmContactAttrs, 'tags')
      const tagsField = Helper.getField(this.fieldDef, 'tags')
      if(tagsField && tagsField.options) {
        tagsField.options = tagsField.options.sort((a, b) => a.label.localeCompare(b.label));
      }
      this.applyCrmAccountFieldDefOptions(this.fieldDef)
      UiHelper.populateOptions(this.fieldDef, {items: StaffHelper.uniqueStaffByUserId(this.staffUsers)}, 'assignee')
      if (this.currentStaffUser && this.currentStaffUser.id) {
        const assigneeField = this.fieldDef.steps[0].fields.find(f => f.key === 'assignee')
        assigneeField.defaultValue = this.currentStaffUser.id
      }
      this.apiData = {
        orgId: this.orgId,
        creatorId: `${this.orgId}-${this.currentStaffUser.id}`,
      }
      this.accountPreviewFieldDef = null
      const accountPreviewTemplate = AppHelper.getSetting(this.orgId, 'CRM_ACCOUNT_PREVIEW_TEMPLATE')
      if (accountPreviewTemplate && this.crmAccountsResult && this.crmAccountsResult.fieldDef) {
        this.accountPreviewFieldDef = JSON.parse(JSON.stringify(this.crmAccountsResult.fieldDef))
      }
      const showExportButton = AppHelper.isSettingEnabled(this.orgId, 'CRM_DASHBOARD_EXPORT_BUTTON')
      if (showExportButton && !this.isMobileView) {
        const isHeaderActionsPresent = this.headerActions.some(action => action.label === 'Export')
        if (!isHeaderActionsPresent) {
          this.headerActions.splice(Math.max(this.headerActions.length - 1, 0), 0, {
            label: 'Export',
            tooltip: 'Export',
            icon: 'download',
            type: 'icon-label',
            onClick: () => {
              const fieldDef = getExportFieldDef(this.orgId)
              fieldDef.title = Helper.getString('exportAccounts')
              this.openModalForAdd(fieldDef)
            }
          })
        }
      }
    } else {
      this.crmAccountsResult = results[0]
      this.syncCrmAccountsListFieldDefAndStats(results[0])
    }
    this.handleNextpage(this.crmAccountsResult)
    const filterFormDefConf = constructFilterFormDefConf({fieldDef: this.fieldDef, crmBulkImportJobs: this.crmBulkImportJobs, currentUser:  this.currentUser, orgId: this.orgId})
    const { filterOptions, searchText, searchFilters, showInternal } = CRMHelper.getFilterOptions(this.fieldDef,filterFormDefConf)
    const p = new Promise((resolve, reject) => {
      try {
        this.setState({
          items: fetchNext ? this.state.items.concat(this.crmAccountsResult.items) : this.crmAccountsResult.items,
          stats: this.crmAccountsResult.stats,
          cards: (this.crmAccountsResult && Array.isArray(this.crmAccountsResult.cards)) ? this.crmAccountsResult.cards.map(c => ({...c, key: c.key || c.label.toLowerCase()})) : null,
          timezone: this.crmAccountsResult._datetimezone || Helper.getLocalTimezone(),
          filterOptions,
          searchText,
          searchFilters,
          showInternal: showInternal,
        }, () => {
          if (!fetchNext && (this.state.searchText || Object.keys(this.state.searchFilters).length > 0)) {
            resolve(this.onSearch(this.state.searchText, this.state.searchFilters, this.state.filterOptions))
          }
          resolve()
        })
      } catch (err) {
        reject(err)
      }
    })
    return p
  }

  handleNextpage(result) {
    // Calculate what the count WILL BE after the new items are concatenated
    const currentItemCount = this.state.items ? this.state.items.length : 0
    const newItemCount = result.items ? result.items.length : 0
    const totalAfterFetch = currentItemCount + newItemCount

    if (totalAfterFetch >= this.maxPaginationItems) {
      this.setState({
        nextPage: null,
        nextPageState: ApiHelper.State.READY
      })
      return
    }

    super.handleNextpage(result)
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) return ''
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }

  handleTabChange = (tab, chartType) => {
    this.setState({
      selectedTabData: {
        ...this.state.selectedTabData,
        [chartType]: tab
      }
    })
  }

  tabbedChart1Actions = () => {
    const dateInterval = CRMHelper.getLabelFromDateId(this.defaultDateInteval.id) || this.defaultDateInteval.id
    return(<span className={css(Styles.chartLabel)}>{dateInterval}</span>)
  }

  handleCardClick = (card) =>{
    let searchFilters = this.state.searchFilters || {}
    let filterOptions = this.state.filterOptions || []
    let searchText = this.state.searchText || ''
    let showInternal = this.state.showInternal || false

    if (Array.isArray(this.state.cards) && this.state.cards.length > 0) {
      const clickedCard = this.state.cards.find(c => c.key === card)
      const filterFormDefConf = constructFilterFormDefConf({fieldDef: this.fieldDef, crmBulkImportJobs: this.crmBulkImportJobs, currentUser:  this.currentUser, orgId: this.orgId})
      const getFilterOptionsResponse = CRMHelper.getFilterOptions(this.fieldDef, filterFormDefConf, `https://filter.slixta.com?${clickedCard.filter}`)
      filterOptions = getFilterOptionsResponse.filterOptions
      searchText = getFilterOptionsResponse.searchText
      searchFilters = Object.assign({}, searchFilters, getFilterOptionsResponse.searchFilters || {})
      showInternal = getFilterOptionsResponse.showInternal
    } else {
      let filterSelectedOperator = 'equals'
      let filter
      if(['leads', 'customers'].includes(card)) {
        filter = 'state'
        let stateSearchFilters = {}
        if(card === 'leads') {
          stateSearchFilters = {
            'state!': ['PROSPECT','UNQUALIFIED']
          }
          filterSelectedOperator = 'not equals'
        } else if(card === 'customers') {
          stateSearchFilters = {
            state: ['CUSTOMER']
          }
          filterSelectedOperator = 'equals'
        }

        if(filter in searchFilters) {
          delete searchFilters.state
        }
        searchFilters = {
          ...searchFilters,
          ...stateSearchFilters
        }
      } else if(card === 'linkedInRequested') {
        filter = 'linkedinConnectionStatus'
        const linkedInConnectionsSearchFilters = {
          linkedinConnectionStatus: ['requested']
        }
        if(filter in searchFilters) {
          delete searchFilters.linkedinConnectionStatus
        }
        searchFilters = {
          ...searchFilters,
          ...linkedInConnectionsSearchFilters
        }
      } else if(card === 'linkedInConnected') {
        filter = 'linkedinConnectionStatus'
        const linkedInConnectionsSearchFilters = {
          linkedinConnectionStatus: ['connected']
        }
        if(filter in searchFilters) {
          delete searchFilters.linkedinConnectionStatus
        }
        searchFilters = {
          ...searchFilters,
          ...linkedInConnectionsSearchFilters
        }
      }

      filterOptions = filterOptions.map((option)=>{
        if(option.key === filter) {
          return { ...option, selectedOperator: filterSelectedOperator };
        }
        return option
      })
    }
    this.onSearch(searchText, searchFilters, filterOptions || [])
  }

  isFilterApplied = (filter) =>{
    let filterEqual
    let filterNotEqual
    if(filter === 'state') {
      filterEqual = this.state.searchFilters.state
      filterNotEqual = this.state.searchFilters['state!']
    } else if(filter === 'linkedinConnectionStatus') {
      filterEqual = this.state.searchFilters.linkedinConnectionStatus
      filterNotEqual = this.state.searchFilters['linkedinConnectionStatus!']
    }
    if ((filterEqual && Array.isArray(filterEqual) && filterEqual.length > 0) || (filterNotEqual && Array.isArray(filterNotEqual) && filterNotEqual.length > 0)) {
      return true
    }
    return false
  }
  chartExpandedTableFilterClickHandler({selectedTabKey, key, label}) {
    let value = label
    if(selectedTabKey === 'suborgs' && key) {
      value = key
    }
    if (key && CRMHelper.isAccountDashboardChartTabKey(selectedTabKey)) {
      value = key
    }

    this.chartExpandedTableFilterHandler(selectedTabKey, value)
  }

  chartExpandedTableRowActons({selectedTabKey, key, label, header}) {
    const actions = []
    let value = label
    if(selectedTabKey === 'suborgs' && key) {
      value = key
    }
    if (key && CRMHelper.isAccountDashboardChartTabKey(selectedTabKey)) {
      value = key
    }

    actions.push(
      <Tooltip title={'Apply Filter'} classes={{ popper: css(Styles.tooltip) }}>
        <IconButton
          disableRipple
          className={css(AppStyles.entityTableLinkIcon)}
          onClick={()=>this.chartExpandedTableFilterHandler(selectedTabKey, value)}
        >
          <i className='material-icons-outlined'>filter_alt</i>
        </IconButton>
      </Tooltip>)

    actions.push(
      <Tooltip title={`Copy ${header}`} classes={{ popper: css(Styles.tooltip) }}>
        <IconButton
          disableRipple
          className={css(AppStyles.entityTableLinkIcon)}
          onClick={() => {
            Helper.copyToClipboard(label)
            if(!Helper.isAndroidDevice()) {
              this.setState({
                showToast: true,
                isErrorToast: false,
                toastMsg: 'Copied to clipboard'
              })
            }
          }}
        >
          <FileCopyOutlinedIcon style={{fontSize: 22}}/>
        </IconButton>
      </Tooltip>)

    if(CRMHelper.isValidURL(label)) {
      actions.push(
        <Tooltip title={'Open'} classes={{ popper: css(Styles.tooltip) }}>
          <IconButton
            disableRipple
            className={css(AppStyles.entityTableLinkIcon)}
            onClick={() => window.open(label, '_blank')}
          >
            <OpenInNewIcon style={{fontSize: 22}}/>
          </IconButton>
        </Tooltip>)
    }

    return actions
  }

  chartExpandedTableFilterHandler(key, cell) {
    let searchFilters = JSON.parse(JSON.stringify(this.state.searchFilters)) || {}
    const filterOptions = JSON.parse(JSON.stringify(this.state.filterOptions)) || []
    let customSearchFilters = {}
    if(key === 'states') {
      if(cell) {
        customSearchFilters = {
          state: [cell]
        }
      }
      delete searchFilters.state
      delete searchFilters['state!']
    } else if(key === 'tags') {
      if(cell) {
        customSearchFilters = {
          tags: [cell]
        }
      }
      delete searchFilters.tags
      delete searchFilters['tags!']
    } else if(key === 'sources') {
      if(cell) {
        customSearchFilters = {
          source: [cell]
        }
      }
      delete searchFilters.source
      delete searchFilters['source!']
    } else if(key === 'utm_campaigns') {
      if(cell) {
        customSearchFilters = {
          utmCampaign: [cell]
        }
      }
      delete searchFilters.utmCampaign
      delete searchFilters['utmCampaign!']
    } else if(key === 'utm_contents') {
      if(cell) {
        customSearchFilters = {
          utmContent: [cell]
        }
      }
      delete searchFilters.utmContent
      delete searchFilters['utmContent!']
    } else if (key) {
      searchFilters = CRMHelper.applyAccountChartFilter(searchFilters, key, cell, { fieldDef: this.fieldDef })
      customSearchFilters = {}
    }

    searchFilters = {
      ...searchFilters,
      ...customSearchFilters
    }
    this.onSearch(this.state.searchText || '', searchFilters, filterOptions || [])

  }

  onCustomFilterSelect(value) {
    const basePath = `${window.location.origin}${this.baseRoute()}/crm-accounts`
    const params = new URLSearchParams(value)
    const url = `${basePath}?${params.toString()}`
    window.history.replaceState({}, '', url);

    this.setState({
      isCustomFiltersDialogOpen: false
    })
    let searchFilters = JSON.parse(JSON.stringify(this.state.searchFilters)) || {}
    const preservedSearchFilters = ['_sortKey']
    searchFilters = Object.fromEntries(
      Object.entries(searchFilters).filter(([key]) => preservedSearchFilters.includes(key))
    )
    let filterOptions = JSON.parse(JSON.stringify(this.state.filterOptions)) || []
    let searchText = ''
    const filterFormDefConf = constructFilterFormDefConf({fieldDef: this.fieldDef, crmBulkImportJobs: this.crmBulkImportJobs, currentUser:  this.currentUser, orgId: this.orgId})
    const result = CRMHelper.getFilterOptions(this.fieldDef, filterFormDefConf)
    if(result) {
      searchText = result.searchText
      filterOptions = result.filterOptions
      searchFilters = result.searchFilters ? {...searchFilters, ...result.searchFilters} : searchFilters
    }
    this.onSearch(searchText, searchFilters, filterOptions)
  }

  compactStatsTabClick(selectedIndex) {
    this.setState({
      compactStatsTabSelected: selectedIndex
    })
  }

  renderAboveTable() {

    const numAccounts = this.state.stats ? (this.state.stats.count || 0) : 0

    const chartTabsData = {
      tabbedChart2: CRMHelper.buildAccountDashboardChartTabs({
        orgId: this.orgId,
        stats: this.state.stats,
        keyPrefix: '',
        label: 'Accounts',
        tableRowActions: this.chartExpandedTableRowActons.bind(this),
        filterClickHandler: this.chartExpandedTableFilterClickHandler.bind(this),
      }),
    }

    // Always keep at least a tags chart if setting yields nothing (e.g. empty stats on first load)
    if (!chartTabsData.tabbedChart2 || chartTabsData.tabbedChart2.length === 0) {
      chartTabsData.tabbedChart2 = [
        {
          chart: 'tags',
          title: 'Tags',
          key: 'tags',
          label: 'Accounts',
          type: 'barChart',
          enableExpansion: true,
          headers: ['Tag', 'Count'],
          searchColumns: ['Tag'],
          searchPlaceholder: 'Search by Tag',
          showPercent: true,
          enableFilter: true,
          tableRowActions: this.chartExpandedTableRowActons.bind(this),
          filterClickHandler: this.chartExpandedTableFilterClickHandler.bind(this)
        },
      ]
    }

    const showStats = this.state.stats && chartTabsData.tabbedChart2 && chartTabsData.tabbedChart2.length > 0

    if(AppHelper.isCrmAccountTrendsMobileEnabled(this.orgId)) {
      chartTabsData.tabbedChart1 = [
        {
          chart: 'accounts',
          title: 'Accounts',
          key: 'accounts_per_day',
          label: 'Accounts',
          type: 'lineChart'
        },
      ]
    }

    const xLabelsDifference = this.getXLabelsDifference(this.state.timezone || Helper.getLocalTimezone())
    let chartData1 = null
    if(chartTabsData.tabbedChart1) {
      const keyTabbedChart1 = chartTabsData.tabbedChart1[this.state.selectedTabData.tabbedChart1].key
      const labelTabbedChart1 = chartTabsData.tabbedChart1[this.state.selectedTabData.tabbedChart1].label
      const typeTabbedChart1 = chartTabsData.tabbedChart1[this.state.selectedTabData.tabbedChart1].type
      const createdTs = this.state.searchFilters.createdTs || this.defaultDateInteval
      chartData1 = CRMHelper.generateMultiChartData({stats: this.state.stats, key: keyTabbedChart1, label: labelTabbedChart1, type: typeTabbedChart1, timezone: this.state.timezone, date: createdTs, orgId: this.orgId})
    }
    let chartData2 = null
    if(chartTabsData.tabbedChart2 && chartTabsData.tabbedChart2.length > 0) {
      const tab2Index = Math.min(
        this.state.selectedTabData.tabbedChart2 || 0,
        chartTabsData.tabbedChart2.length - 1
      )
      const keyTabbedChart2 = chartTabsData.tabbedChart2[tab2Index].key
      const labelTabbedChart2 = chartTabsData.tabbedChart2[tab2Index].label
      const typeTabbedChart2 = chartTabsData.tabbedChart2[tab2Index].type
      chartData2 = CRMHelper.generateMultiChartData({stats: this.state.stats, key: keyTabbedChart2, label: labelTabbedChart2, type: typeTabbedChart2, timezone: this.state.timezone, orgId: this.orgId})
    }

    let cardsData = []

    if (Array.isArray(this.state.cards) && this.state.cards.length > 0) {
      this.state.cards.forEach((card, i) => {
        cardsData.push({
          key: card.key,
          text: (card.key && card.key.startsWith('ltv')) ? Helper.formatLargeNumber(card.ltv || 0) : card.doc_count || 0,
          subText: card.label,
          cardIndex: i + 1,
          cardId: card.key,
          padding: 'small',
          handleCardClick: card.filter && card.doc_count > 0 && !this.isFilterApplied(card.filter) && this.handleCardClick.bind(this)
        })
      })
      const maxCardsPerRowCdpSettings = AppHelper.getSetting(this.orgId, 'CRM_CUSTOM_STAT_CARDS_MAX_PER_ROW')
      let maxCardsPerRow = 7
      if(!isNaN(maxCardsPerRowCdpSettings) && maxCardsPerRowCdpSettings !== '' && maxCardsPerRowCdpSettings > 0) {
        maxCardsPerRow = Number(maxCardsPerRowCdpSettings)
      }
      cardsData = CRMHelper.splitCardsIntoBalancedRows(cardsData, maxCardsPerRow)
    } else {
      const cardsFirstRow = []
      if(numAccounts > 0) {
        cardsFirstRow.push({
          key: 'accounts',
          text: numAccounts,
          subText: 'Accounts',
          cardIndex: 1,
          padding: 'small',
        })
      }
      cardsData.push(cardsFirstRow)
    }

    const isCompactStatsEnabled = AppHelper.isCompactStatsCardsMobileEnabled(this.orgId)
    const customToggleButtonData = [
      {
        buttonText: 'Stats',
        onClick: this.compactStatsTabClick.bind(this,0)
      },
      {
        buttonText: 'Charts',
        onClick: this.compactStatsTabClick.bind(this,1)
      }
    ]
    if(AppHelper.isCrmAccountTrendsMobileEnabled(this.orgId)) {
      customToggleButtonData.push({
        buttonText: 'Trends',
        onClick: this.compactStatsTabClick.bind(this,2)
      })
    }
    return <>
      <div className={css(Styles.aboveTableContainer)}>
        {this.isMobileView && isCompactStatsEnabled && (<div className={css(Styles.compactStatsTabsContainer)}>
          <CustomToggleButton buttonData={customToggleButtonData}
            toggleButtonSelected={this.state.compactStatsTabSelected}
            fullWidth={true}
          />
        </div>)}
        {((cardsData.flat().length > 4 && !this.isMobileView) && cardsData && cardsData.length > 0) && (<div className={css(Styles.numericStatItemRow)}>
          {cardsData.map((row)=>(<Grid container className={css(Styles.numericStatContainerRow)}>
            {row.map((card)=>(
              <StatCard
                xs={4}
                sm={1}
                key={card.key}
                text={card.text}
                subText={card.subText}
                cardIndex={card.cardIndex}
                padding={card.padding}
                cardId={card.cardId}
                handleCardClick={card.handleCardClick}
                containerStyle={Styles.statsCardContainer}
                cardStyle={Styles.statsCard}
              />))}
          </Grid>))}
        </div>)}
        {((cardsData.flat().length > 4 && this.isMobileView && (!isCompactStatsEnabled || this.state.compactStatsTabSelected === 0))) && cardsData && cardsData.length > 0 && (<div className={css(Styles.numericStatItemRow)}>
          {cardsData.map((row, index) => {
            const rowGroups = Array.from({ length: Math.ceil(row.length / 3) })
            return (
              <Grid key={index}>
                {rowGroups.map((_, groupIndex) => {
                  const groupItems = row.slice(groupIndex * 3, groupIndex * 3 + 3)
                  return (
                    <Grid
                      key={groupIndex}
                      container
                      className={css(Styles.numericStatContainerRow)}
                    >
                      {groupItems.map((card) => (
                        <StatCard
                          xs={12 / groupItems.length}
                          sm={12 / groupItems.length}
                          key={card.key}
                          text={card.text}
                          subText={card.subText}
                          cardIndex={card.cardIndex}
                          padding={card.padding}
                          cardId={card.cardId}
                          handleCardClick={card.handleCardClick}
                          containerStyle={Styles.statsCardContainer}
                          cardStyle={Styles.statsCard}
                        />
                      ))}
                    </Grid>
                  )
                })}
              </Grid>
            )
          })}
        </div>)}
        {showStats &&
        <div className={css(Styles.statContainer)}>
          {((cardsData.flat().length <= 4 && (!this.isMobileView || (this.isMobileView && (!isCompactStatsEnabled || this.state.compactStatsTabSelected === 0)))) && cardsData && cardsData.length > 0) && (<div className={css(Styles.numericStatItem)}>
            {cardsData.map((row)=>(<Grid container className={css(Styles.numericStatContainer)}>
              {row.map((card)=>(
                <StatCard
                  xs={12 / row.length}
                  sm={12}
                  key={card.key}
                  text={card.text}
                  subText={card.subText}
                  cardIndex={card.cardIndex}
                  padding={card.padding}
                  cardId={card.cardId}
                  handleCardClick={card.handleCardClick}
                  cardStyle={Styles.statsCardSmall}
                />))}
            </Grid>))}
          </div>)}
          {chartData2 && (!this.isMobileView || (this.isMobileView && (!isCompactStatsEnabled || this.state.compactStatsTabSelected === 1))) && (<div className={(cardsData.flat().length <= 4) ? css(Styles.barStatItem) : css(Styles.barStatItemRow)}>
            <TabChartCard chartType={'tabbedChart2'} tabsData={chartTabsData} xLabelsDifference={xLabelsDifference} selectedTabData={this.state.selectedTabData} handleTabChange={this.handleTabChange} chartData={chartData2} isMultiChart={true} percentTotal={numAccounts}/>
          </div>)}
          {chartData1 && (!this.isMobileView || (this.isMobileView && (!isCompactStatsEnabled || this.state.compactStatsTabSelected === 2))) && (<div className={(cardsData.flat().length <= 4) ? css(Styles.pieStatItem) : css(Styles.pieStatItemRow)}>
            <TabChartCard chartType={'tabbedChart1'} tabsData={chartTabsData} xLabelsDifference={xLabelsDifference} selectedTabData={this.state.selectedTabData} handleTabChange={this.handleTabChange} chartData={chartData1} isMultiChart={true} chartActions={!this.state.searchFilters.createdTs && this.tabbedChart1Actions.bind(this)}/>
          </div>)}
        </div>
        }
        <SearchBar
          hint={this.isMobileView ? 'Search' : 'Type company name, etc.'}
          onSearch={this.onSearch.bind(this)}
          onList={this.onList.bind(this)}
          onFiltersBtnClick={this.onFiltersBtnClick.bind(this)}
          onCustomFiltersBtnClick={this.onCustomFiltersBtnClick.bind(this)}
          getUpdatedSearchFilters={CRMHelper.getUpdatedSearchFilters}
          getTransformedKey={CRMHelper.getTransformedKey}
          searchText={this.state.searchText}
          searchFilters={this.state.searchFilters}
          searchFilterActions={[
            {
              label: 'Save Filter',
              buttonStyle: Styles.actionButton,
              onClick: () => {
                const searchFilters = JSON.parse(JSON.stringify(this.state.searchFilters || {}))
                const searchFilterTimeZone = Array.isArray(searchFilters._datetimezone) ? searchFilters._datetimezone[0] : searchFilters._datetimezone
                delete searchFilters._datetimezone
                const timezone = searchFilterTimeZone || Helper.getLocalTimezone()
                Object.entries(searchFilters).forEach(([k, v]) => { if (!v) { delete searchFilters[k] } })
                const filtersQs = FilterHelper.processFilterFormSubmission({filterFormData: searchFilters, filterQueryData: this.state.searchText || '', timezone})

                const customFilters = CRMHelper.getCustomFilters(this.orgId,this.pageKey) || []
                this.apiData = { orgId: this.orgId, filtersQs }
                this.formDefinition = getSaveFilterFieldDef(customFilters)
                this.setState({ modalOpen: true });
              }
            }
          ]}
          filterOptions={this.state.filterOptions}
          searchButtonLabel='Search'
          listButtonLabel={this.isMobileView ? 'Clear' : 'Clear Filters'}
          isQuickFilterDisabled={true}
          isAppliedFiltersVisible={true}
          timezone={this.state.timezone}
          getLabelFromDateId={CRMHelper.getLabelFromDateId}
          autoRefresh={false}>
        </SearchBar>
      </div>
      {this.isMobileView && <Fab
        aria-label='add'
        disableRipple
        onClick={() => this.openModalForAdd()}
        className={css(Styles.floatingActionButton)}>
        <AddIcon />
      </Fab>}
    </>
  }

  renderBelowTable() {
    return (
    <>
      {this.state.isEntityDialogOpen && this.renderEntityDialog()}
      {this.state.isCustomFiltersDialogOpen && this.renderCustomFiltersDialog()}
    </>
    )
  }

  renderEntityDialog() {
    return (
      <PageDialog
        PageComponent={CrmAccount}
        PageComponentProps={this.state.entityDialogProps}
        title={this.state.entityDialogTitle}
        isOpen={this.state.isEntityDialogOpen}
        fullWidth={this.state.entityDialogFullWidth}
        fullHeight={this.state.entityDialogFullHeight}
        onClosed={() => {
          const dialogApiData = {
            responseBody: { id: this.state.entityId }
          }
          this.setState({ isEntityDialogOpen: false, dialogApiData, loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD }, () => this.fetchItem())
        }}
      />
    );
  }

  renderCustomFiltersDialog() {
    const customFilters = CRMHelper.getCustomFilters(this.orgId, this.pageKey) || []
    return (
      <PageDialog
        PageComponent={CRMHelper.CustomFilters}
        PageComponentProps={this.state.customFiltersDialogProps}
        title={'Quick Filters'}
        fullWidth={customFilters.length > 5}
        maxWidth='sm'
        fullWidthDialog={false}
        isOpen={this.state.isCustomFiltersDialogOpen}
        onClosed={() => {
          this.setState({ isCustomFiltersDialogOpen: false })
        }}
      />
    );
  }

  onSearch = (searchText = '', searchFilters = {}, filterOptions = []) => {
    Object.keys(searchFilters).forEach(k => {
      if (Array.isArray(searchFilters[k]) && searchFilters[k].length === 0) {
        delete searchFilters[k]
      } else if (!Array.isArray(searchFilters[k]) && searchFilters[k] === '') {
        delete searchFilters[k]
      }
    })
    CRMHelper.updateWindowUrl(searchText, searchFilters, this.state.timezone)
    this.setState({
      fetchState: ApiHelper.State.LOADING,
      searchText: searchText,
      searchFilters: searchFilters,
      filterOptions: filterOptions,
    })
    const searchFiltersQuery = CRMHelper.constructSearchFiltersQuery(searchFilters, filterOptions, this.state.showInternal, this.restrictViewCrmFilters.assignee)
    const formattedSearchText = searchText.trim().replace(/^\+(\d+)/, '$1').replace(/:/g, '\\:')
    const queryParams = { orgId: this.orgId, query: formattedSearchText, pb: 'fieldDef', includeCustomFields: 'true', limit: 100, _datetimezone: Helper.getLocalTimezone(), _dateinterval: this.getDateInterval(searchFilters, this.state.timezone), _aggTermsLimit: this.aggregationTermsLimit, ...searchFiltersQuery }
    const options = { key: 'crmaccounts', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['crmaccounts'], queryParams }
    this.toFetch[0] = options

    return ApiHelper.callAwait(options).then(result => {
      this.handleNextpage(result)
      if (result && result.stats) {
        this.crmAccountsStats = result.stats
      }
      if (result && result.fieldDef) {
        this.fieldDef = this.formDefinition = result.fieldDef
        if (this.crmAccountsResult) {
          this.crmAccountsResult.fieldDef = result.fieldDef
        }
        Helper.loadAttrOptions(this.fieldDef, this.crmContactAttrs, 'tags')
        this.applyCrmAccountFieldDefOptions(this.fieldDef)
        UiHelper.populateOptions(this.fieldDef, { items: StaffHelper.uniqueStaffByUserId(this.staffUsers) }, 'assignee')
      }
      this.setState({
        items: (result && result.items) || [],
        stats: result.stats,
        cards: (result && Array.isArray(result.cards)) ? result.cards.map(c => ({...c, key: c.key || c.label.toLowerCase()})) : null,
        timezone: result._datetimezone || Helper.getLocalTimezone(),
        fetchState: ApiHelper.State.READY,
      })
    }).catch(err => {
      console.log('Search Error:', err)
    })
  }

  onFiltersBtnClick = (searchText = '', searchFilters = {}, filterOptions = [], isFilterChanged = false, isSearchTextChanged = false) => {
    this.setState({
      searchText: searchText,
      searchFilters: searchFilters,
      filterOptions: filterOptions,
    }, () => {
      const filtersFieldDef = CRMHelper.getFilterDialogFieldDef(filterOptions)
      if(filtersFieldDef) {
        UiHelper.filterFieldForMainOrgOptions(filtersFieldDef, 'assignee', this.subOrgs, this.staffUsers, this.orgId)
      }

      const normalizedSearchFilters = CRMHelper.getNormalizedSearchFilters(filterOptions, searchFilters)
      this.openModalForFilter(normalizedSearchFilters, filtersFieldDef);
    })
  }

   onCustomFiltersBtnClick = () => {
     this.setState({
       isCustomFiltersDialogOpen: true,
       customFiltersDialogProps: {
         pageTitle: 'Quick Filters',
         orgId: this.orgId,
         pageKey:this.pageKey,
         onCustomFilterSelect: this.onCustomFilterSelect.bind(this),
         onCustomFilterDelete: (filter) => CRMHelper.handleCustomFilterDelete(this, filter),
         onCustomFilterRename: (filter, newLabel) => CRMHelper.handleCustomFilterRename(this, filter, newLabel),
       }
     })
   }

  onList = () => {
    CRMHelper.updateWindowUrl()
    this.toFetch[0] =  { key: 'crmaccounts', ms: AppConfig.CDP_MS.ENDPOINT, method: 'GET', paths: ['crmaccounts'], queryParams: { orgId: this.orgId, pb: 'fieldDef', includeCustomFields: 'true', limit: 100, ...this.restrictViewCrmFilters, _datetimezone: Helper.getLocalTimezone(), _dateinterval: this.dateMap[this.defaultDateInteval.id], _aggTermsLimit: this.aggregationTermsLimit } }
    this.setState({
      fetchState: ApiHelper.State.LOADING,
      searchText: '',
      searchFilters: {},
    }, () => this.fetchItems())
  }

  openModalForAdd(fieldDef, apiData = {}) {
    this.apiData = {
      orgId: this.orgId,
      creatorId: `${this.orgId}-${this.currentStaffUser.id}`,
      ...apiData,
    }

    const sourceFieldDef = fieldDef || this.fieldDef
    const fieldDefinition = sourceFieldDef
      ? CRMHelper.prepareCrmAccountModalFieldDef(sourceFieldDef, {
        title: fieldDef ? undefined : Helper.getString('addAccount'),
        isMobileView: this.isMobileView,
        stats: this.crmAccountsStats || (this.state && this.state.stats),
        crmContactAttrs: this.crmContactAttrs,
        optionsSourceFieldDef: fieldDef || this.fieldDef,
      })
      : null

    if (fieldDefinition) {
      UiHelper.filterFieldForMainOrgOptions(fieldDefinition, 'assignee', this.subOrgs, this.staffUsers, this.orgId)
    }
    super.openModalForAdd(fieldDefinition)
  }

  onAdd() {
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths: ['miscs', 'create-crm-account'],
        queryParams: { orgId: this.orgId }
      }),
    });
  }

  openModalForEdit(current) {
    this.apiData = {
      orgId: this.orgId,
    }
    const baseFieldDef = this.fieldDef || this.crmAccountsResult.fieldDef
    this.applyCrmAccountFieldDefOptions(baseFieldDef)
    const fieldDef = CRMHelper.buildCrmAccountEditFieldDef(baseFieldDef, current, {
      isMobileView: this.isMobileView,
      stats: this.crmAccountsStats || this.state.stats,
      crmContactAttrs: this.crmContactAttrs,
      optionsSourceFieldDef: baseFieldDef,
    })
    UiHelper.filterFieldForMainOrgOptions(fieldDef, 'assignee', this.subOrgs, this.staffUsers, this.orgId)
    super.openModalForEdit(current, fieldDef)
  }

  openModalForInfo(current) {
    this.apiData = {
      orgId: this.orgId,
    }
    let fieldDef = JSON.parse(JSON.stringify(this.crmAccountsResult.fieldDef))
    fieldDef.title = (current.title || current.accountKey)
    fieldDef.tooltipFields = ['title']
    const crmAccountInfoFieldsCdpSettings = AppHelper.getSetting(this.orgId, 'CRM_ACCOUNT_INFO_FIELDS')
    if(crmAccountInfoFieldsCdpSettings) {
      const crmAccountInfoFields = crmAccountInfoFieldsCdpSettings && crmAccountInfoFieldsCdpSettings.split(',').map(item => item.trim()).filter(Boolean)
      if(crmAccountInfoFields && crmAccountInfoFields.length > 0) {
        fieldDef = Helper.getFilteredFieldDef(fieldDef, crmAccountInfoFields)
      }
    }
    this.setState({
      current: current
    })
    super.openModalForInfo(current,fieldDef)
  }

  onEdit(current) {
    CRMHelper.normalizeAssigneeFieldValues(this.formDefinition, ['assignee'])
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths: ['miscs', 'update-crm-account'],
        queryParams: { id: current.id, orgId: this.orgId }
      }),
    });
  }

  onActions = (current) => {
    const actions = []
    if (AppHelper.isEntityInPlaceDrillDown(this.orgId)) {
      actions.push(
        <Tooltip title={'Interactions'} placement='top-start' key={current.id + 'inPlaceDD'}>
          <span className={css(AppStyles.entityTableLinkIcon)}>
            <i className='material-icons-outlined' onClick={(e) => {
              e.stopPropagation()
              this.openInteractionsDialog(current)
            }}>contact_page</i>
          </span>
        </Tooltip>
      )
    }
    if(AppHelper.isCrmAccountDrillDownButtonEnabled(this.orgId)) {
      actions.push(
        <Link to={this.baseRoute() + '/crm-accounts/' + current.id} key={current.id} onClick={(e) => e.stopPropagation()}>
          <Tooltip title='Detail Page' placement='top-start'>
            <span className={css(AppStyles.entityTableLinkIcon)}>
              <i className='material-icons-outlined'>arrow_forward_ios</i>
            </span>
          </Tooltip>
        </Link>
      )
    }
    return actions
  }

  onRowClick(current) {
    if(AppHelper.isCrmAccountClickableEnabled(this.orgId)) {
      return this.openInteractionsDialog(current)
    }
    return null
  }

  async onFilterSubmit(options) {
    const { formType, formData, filterOptions, caller } = options
    const searchFilters = JSON.parse(JSON.stringify(formData));
    const updatedSearchFilters = CRMHelper.getUpdatedSearchFilters(filterOptions, searchFilters)

    const underscoreSearchFilters = Object.keys(this.state.searchFilters || {})
      .filter(key => key.startsWith('_'))
      .reduce((acc, key) => {
        acc[key] = this.state.searchFilters[key]
        return acc
      }, {})

    const mergedSearchFilters = {...underscoreSearchFilters, ...updatedSearchFilters}
    this.setState({modalOpen: false })
    return this.onSearch(this.state.searchText, mergedSearchFilters, filterOptions)
  }

  async customSubmitForSaveFilter(options) {
    const { formData, caller } = options
    const currentSettingString = AppHelper.getSetting(this.orgId, 'CRM_ACCOUNT_CUSTOM_FILTERS') || ''
    const currentFilters = CRMHelper.getCustomFilters(this.orgId,this.pageKey) || []

    const newQueryString = formData.filtersQs
    const filterName = formData._chosenFilter === '_new' ? formData.filterNickname : formData._chosenFilter

    const existingIndex = currentFilters.findIndex(f => f.label === filterName)
    if (existingIndex > -1) {
      currentFilters[existingIndex].value = newQueryString
    } else {
      currentFilters.push({ label: filterName, value: newQueryString })
    }

    const newSettingString = currentFilters.map(f => `${f.label}: ${f.value}`).join(' | ')

    CdpHelper.setSetting({
      orgId: this.orgId,
      settingsKey: 'CRM_ACCOUNT_CUSTOM_FILTERS',
      settingsValue: newSettingString,
      previousKey: 'CRM_ACCOUNT_CUSTOM_FILTERS',
      previousValue: currentSettingString
    }).then(response => {
      if (response.success) {
        caller.setState({ fetchState: ApiHelper.State.READY })
        this.setState({
          modalOpen: false,
          showToast: true,
          toastMsg: 'Filter saved',
        })
      } else {
        caller.setState({ fetchState: ApiHelper.State.ERROR, errMsg: response.error })
      }
    }).catch(err => {
      caller.setState({ fetchState: ApiHelper.State.ERROR, errMsg: Helper.getErrorMsg(err) })
    })
  }

  async customSubmitForExport(options) {
    const { formData, caller } = options
    const jsonBody = formData
    if (formData.mmyyyy === 'all_time') {
      delete formData.mmyyyy
      jsonBody.fromCreatedTs = 0
      jsonBody.toCreatedTs = Helper.currentTs()
    } else if (formData.mmyyyy === 'date_range') {
      const mode = formData.exportMode
      const tz = Helper.getLocalTimezone()
      const exportDateRange = formData.exportDateRange
      delete formData.exportDateRange
      delete formData.exportMode
      delete formData.mmyyyy
      let fromTs = 0
      let toTs = Helper.currentTs()
      if (exportDateRange && exportDateRange.startValue && exportDateRange.endValue) {
        fromTs = moment.tz(exportDateRange.startValue, tz).startOf('day').valueOf()
        toTs = moment.tz(exportDateRange.endValue, tz).endOf('day').valueOf()
      }

      if (mode === 'MODIFIED') {
        jsonBody.fromModifiedTs = fromTs
        jsonBody.toModifiedTs = toTs
      } else {
        jsonBody.fromCreatedTs = fromTs
        jsonBody.toCreatedTs = toTs
      }
    } else if (formData.mmyyyy) {
      delete formData.exportDateRange
      delete formData.exportMode
    }
    ApiHelper.callAwait({
      method: 'POST',
      endPoint: ApiHelper.makeUrlPath2({
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths: ['miscs', 'export-crm-account'],
        queryParams: {orgId: this.orgId},
      }),
      jsonBody: formData,
    }).then(response => {
      window.open(this.baseRoute() + '/jobscrm', '_self')
    }).catch(err => {
      const errorMessage = Helper.getErrorMsg(err);
      caller.setState({
        fetchState: ApiHelper.State.ERROR,
        errMsg: errorMessage,
        success: false,
        message: errorMessage,
        inProcess: false
      })
    })
  }

  async customSubmit(options) {
    const { formType, formData, caller } = options
    if (formData._isAccountEnrich) { return UiHelper.customSubmitForEnrichAccount.call(this, options) }
    if (formData._chosenFilter) { return this.customSubmitForSaveFilter(options) }
    if (formData.mmyyyy || formData.exportMode) { return this.customSubmitForExport(options) }
    if (formData._isMultiSelectExport) { return this.customSubmitForExport(options) }
    if (formData.importCsv) {
      if(formData && formData.updateExisting === 'YES' && formData.assignee) {
        delete formData.assignee
      }
      try {
        await ApiHelper.callAwait({
          method: 'POST',
          endPoint: ApiHelper.makeUrlPath2({
            ms: AppConfig.CDP_MS.ENDPOINT,
            paths: ['miscs', 'create-crm-account'],
            queryParams: { orgId: this.orgId },
          }),
          jsonBody: formData,
        })
        window.open(this.baseRoute() + '/jobscrm', '_self')
        return
      } catch(err) {
        const errorMessage = Helper.getErrorMsg(err);
        caller.setState({
          fetchState: ApiHelper.State.ERROR,
          errMsg: errorMessage,
          success: false,
          message: errorMessage,
          inProcess: false
        })
        return
      }
    }

    return caller.submit.call(caller, formData, true);
  }

  checkBoxMenuActions = (checkedItems) => {
    const actions = []
    const showExportButton = AppHelper.isSettingEnabled(this.orgId, 'CRM_DASHBOARD_EXPORT_BUTTON')
    const isAccountEnrichEnabled = AppHelper.isSettingEnabled(this.orgId, 'CRM_ACCOUNT_ENRICH')
    if (showExportButton && !this.isMobileView) {
      actions.push(UiHelper.actionButton({
        key: 'export',
        action: () => UiHelper.openModalForExportAccounts.call(this, checkedItems),
        title: 'Export',
        icon: 'download',
      }))
    }
    if (isAccountEnrichEnabled && !this.isMobileView && !this.subOrgId) {
      actions.push(UiHelper.actionButton({
        key: 'enrich',
        action: () => UiHelper.openModalForEnrichAccount.call(this, checkedItems),
        title: 'Enrich',
        icon: 'dynamic_form',
      }))
    }
    return actions
  }
}

const Styles = StyleSheet.create({
  spanValue: {
    fontSize: 14
  },
  infoCellMobile: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 2,
  },
  infoFieldMobile: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '50vw'
  },
  cellWidthSmall: {
    width: '6%',
  },
  cellWidthNormal: {
    width: '40%',
  },
  line2: {
    fontSize: 10,
    color: 'grey'
  },
  statusBadge: {
    backgroundColor: AppTheme.primaryBg,
    // color: 'white',
    padding: 5,
    borderRadius: 10,
    margin: 5,
    wordBreak: 'keep-all',
    display: 'block',
    textAlign: 'center'
  },
  statusBadgeCustomer: {
    backgroundColor: '#3BDE86',
    color: '#222222'

  },
  line3: {
    fontSize: 12,
    color: 'grey'
  },
  aboveTableContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  statContainer: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    gap: 8,
    justifyContent: 'flex-start',
    marginTop: 0,
    marginBottom: 6,
    maxWidth: window.innerWidth - AppTheme.drawerWidth - (AppTheme.adminPagePadding * 2),
    '@media (max-width: 1024px)': {
      justifyContent: 'center',
      maxWidth: '97vw'
    },
    '@media (max-width: 767px)': {
      justifyContent: 'center',
      marginTop: 0,
      maxWidth: '95vw'
    },
  },
  numericStatItem: {
    flex: '0 0 20%',
    maxWidth: '20%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 1024px)': {
      flex: '1 1 100%',
      maxWidth: '98%',
      width: '98%',
      alignSelf: 'center',
      paddingTop: 8,
      paddingBottom: 8,
    },
  },
  numericStatItemRow: {
    width: '100%',
    marginBottom: 4,
    '@media (max-width: 1024px)': {
      alignSelf: 'center',
      paddingTop: 8,
      paddingBottom: 8,
    },
  },
  pieStatItem: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 1024px)': {
      flex: '1 1 100%',
      width: '98%',
      alignSelf: 'center',
      paddingBottom: 16,
    },
  },
  barStatItem: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 1024px)': {
      flex: '1 1 100%',
      width: '98%',
      alignSelf: 'center',
      paddingBottom: 16,
    },
  },
  pieStatItemRow: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 1024px)': {
      flex: '1 1 100%',
      width: '98%',
      alignSelf: 'center',
      paddingBottom: 16,
    },
  },
  barStatItemRow: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 1024px)': {
      flex: '1 1 100%',
      width: '98%',
      alignSelf: 'center',
      paddingBottom: 16,
    },
  },
  numericStatContainer: {
    height: '100%',
    maxHeight: '100%',
    maxWidth: AppTheme.viewPort,
    textAlign: 'center',
    display: 'flex',
    border: '1px solid ' + AppTheme.borderColor,
    padding: 16,
    margin: 0,
    borderRadius: AppTheme.borderRadius,
    gap: 5,
    //justifyContent: 'left',
    '@media (max-width: 1024px)': {
      border: 0,
      padding: '0px 0px',
      gap: 0
    },
  },
  numericStatContainerRow: {
    height: '100%',
    maxHeight: '100%',
    maxWidth: AppTheme.viewPort,
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0px 4px 0px',
    margin: 0,
    gap: 5,
    //justifyContent: 'left',
    '@media (max-width: 1024px)': {
      padding: '2px 0px',
      gap: 0
    },
  },
  numericStatHorizontalContainer: {
    height: '100%',
    maxHeight: '100%',
    maxWidth: AppTheme.viewPort,
    display: 'flex',
    gap: 10
  },
  barChartContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  phoneValue: {
    cursor: 'pointer',
    color: AppTheme.primaryColor,
  },
  fieldActionsContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 10,
    '@media (max-width: 767px)': {
      gap: 5
    },
  },
  connectButton: {
    color: AppTheme.buttonTextColor,
    backgroundColor: AppTheme.buttonBg,
    fontWeight: 600,
    padding: 7,
    fontSize: 14,
    textTransform: 'none',
    minWidth: 80,
    height: 25,
    marginBottom: 6,
    borderRadius: 6,
    '@media (max-width: 767px)': {
      minWidth: 80,
    }
  },
  connectButtonText: {
    color: AppTheme.buttonTextColor,
    fontWeight: 600,
    fontSize: 14,
    margin: 0,
    textTransform: 'none',
    '@media (max-width: 767px)': {
      fontSize: 12,
    },
  },
  actionsSelect: {
    minWidth: 140,
    paddingLeft: 10,
    paddingRight: 5,
    border: '1px solid #E5E5E5',
    textDecoration: 'none',
    borderRadius: 16,
    fontSize: 14,
    height: 25,
    marginBottom: 6,
    color: '#222222',
    '@media (max-width: 767px)': {
      minWidth: 110,
      fontSize: 12,
    },
  },
  actionsSelection: {
    color: '#222222',
    ':focus': {
      backgroundColor: 'inherit !important',
    }
  },
  actionsSelectMenu: {
    color: '#222222',
    marginTop: 3,
  },
  actionCircularProgress: {
    paddingBottom: 2,
    paddingLeft: 4
  },
  link: {
    textDecoration: 'none',
  },
  floatingActionButton: {
    position: 'fixed',
    bottom: 90,
    right: 14,
    zIndex: 1300,
    backgroundColor: AppTheme.buttonBg,
    color: AppTheme.buttonTextColor,
    boxShadow: 'none',
    ':active': {
      boxShadow: 'none',
    },
  },
  chartLabel: {
    fontSize: 10,
    color: 'grey'
  },
  tooltip: {
    zIndex: 9999999999
  },
  statsCardContainer: {
    maxWidth: 'unset',
    flex: 1
  },
  statsCard: {
    width: '100%',
  },
  statsCardSmall : {
    width: '100%',
    height: 'auto',
    '@media (max-width: 1024px)': {
      width: '99%'
    },
    '@media (max-width: 767px)': {
      width: '99%'
    },
  },
  previewLabel: {
    fontWeight: 500
  },
  compactStatsTabsContainer: {
    marginTop: 8,
    marginBottom: 5
  },
  actionButton: {
    height: 38,
    minWidth: 120,
    padding: 13,
    fontSize: 14,
    textTransform: 'capitalize',
    color: AppTheme.primaryColor,
    border: `1px solid ${AppTheme.borderColor}`,
    backgroundColor: 'transparent',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 500,
    ':hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
})